import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Create } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ModulePage } from '~/components/ModulePage/ModulePage';
import { ModulePageHeader } from '~/components/ModulePageHeader/ModulePageHeader';
import { ModuleSlot } from '~/components/ModuleSlot/ModuleSlot';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { ModuleSlotId } from '~/modules';
import { RouteId } from '~/routes';

import { modulesConfig } from './NegotiationCaseCreate.modules';
import { NEGOTIATION_CASE_CREATE_MUTATION } from './NegotiationCaseCreate.mutation.gql';
import {
  negotiationCreateFormDataDefault as defaultValues,
  negotiationCreateFormDataSchema,
  NegotiationCreateFormData,
} from './NegotiationCaseCreate.schema';
import { prepareDemandRequest, prepareNegotiationCases } from './NegotiationCaseCreate.util';

export const NegotiationCaseCreate = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationCreate' });
  const { onError } = useCustomSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const track = useTrackInteraction();
  const formId = useUuid();
  const form = useForm<NegotiationCreateFormData>({
    resolver: zodResolver(negotiationCreateFormDataSchema),
    defaultValues,
    mode: 'all',
  });

  const [create, { loading: saving }] = useMutation(NEGOTIATION_CASE_CREATE_MUTATION, {
    onError,
  });

  const onSubmit: SubmitHandler<NegotiationCreateFormData> = (submitData) => {
    if (submitData) {
      (async () => {
        const { data } = await create({
          variables: {
            data: {
              responsibleUsers: user?.profile.sub ? [user.profile.sub] : undefined,
              requestedBy: user?.profile.user_id,
              demandRequest: prepareDemandRequest(submitData),
              negotiationCases: prepareNegotiationCases(submitData),
            },
          },
        });
        track(Target.NEGOTIATION_CASE, Action.ADDED, {
          id: data?.tesseract.demandCreate.negotiationCases.nodes[0]?.id,
          demandId: data?.tesseract.demandCreate.id,
        });
        if (data?.tesseract.demandCreate.negotiationCases.nodes[0]?.id) {
          navigate(RouteId.negotiationDetail(data?.tesseract.demandCreate.negotiationCases.nodes[0]?.id));
        }
      })();
    }
  };

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} autoComplete="off" noValidate>
        <ModulePage
          header={
            <ModulePageHeader
              title={t('title')}
              right={
                <Stack
                  spacing={3}
                  direction="row"
                  sx={{
                    overflow: 'hidden',
                  }}
                >
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={RouteId.negotiationCases}
                    data-intercom-target="NegotiationCreate/Cancel"
                  >
                    {t('cancelButton')}
                  </Button>
                  <Button
                    loading={saving}
                    loadingPosition="start"
                    startIcon={<Create />}
                    variant="contained"
                    color="primary"
                    type="submit"
                    form={formId}
                    data-intercom-target="NegotiationCreate/Save"
                    data-testid="NegotiationCreate/Save"
                  >
                    {t('createButton')}
                  </Button>
                </Stack>
              }
            />
          }
          modules={<ModuleSlot id={ModuleSlotId.MAIN} modules={modulesConfig[ModuleSlotId.MAIN]} />}
        />
      </form>
    </FormProvider>
  );
};
